import { useEffect, useMemo, useRef } from 'react';
import { PX_PER_MINUTE } from '@frontend/calendar-grid';
import { GridCalendarEvent } from '../../../components/calendar-view/types';
import { useCalendarDeepLinkParams } from '../../../hooks/calendar/use-calendar-deep-link-params';
import { useCurrentDateQueryParam } from '../../../hooks/scheduler-v3/use-current-date-query-param';
import { scrollToPositionForCalendarGrid } from '../../../utils/calendar-helpers';

type UseCalendarGridAutoScrollParams = {
  events: GridCalendarEvent[];
  isLoading: boolean;
  calendarDate: string;
  zoomLevel: number;
  primaryTimezone?: string;
};

/** Drives initial auto-scroll for the CalendarGrid view after events finish loading. */
export const useCalendarGridAutoScroll = ({
  events,
  isLoading,
  calendarDate,
  zoomLevel,
  primaryTimezone,
}: UseCalendarGridAutoScrollParams) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const hasScrolledToPosition = useRef(false);

  const eventIds = useMemo(() => events.map((e) => e.id), [events]);
  const { isDeepLinkedEvent: isDeepLinkedEventPresent } = useCalendarDeepLinkParams(eventIds);

  const pxPerMinute = Math.round(PX_PER_MINUTE * zoomLevel);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isLoading || hasScrolledToPosition.current || isDeepLinkedEventPresent) return;
      scrollToPositionForCalendarGrid(wrapperRef.current, events, pxPerMinute, calendarDate, primaryTimezone);
      hasScrolledToPosition.current = true;
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isLoading, isDeepLinkedEventPresent, events, pxPerMinute, calendarDate, primaryTimezone]);

  // Sync the currentDate query param with the calendarDate in the store
  useCurrentDateQueryParam();

  return { wrapperRef, isDeepLinkedEventPresent };
};
